import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { fetchPrinciples, fetchResourceTypes } from '@/api'
import { PageHead, PageShell, Section } from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import type { DeclarationStatus, PrincipleGroup, Scope, VerificationLevel } from '@/types'

const terms = [
    'fair',
    'principle',
    'fip',
    'resource',
    'declaration',
    'community',
    'matrix',
    'metadata',
    'pid',
]
const groups: PrincipleGroup[] = ['F', 'A', 'I', 'R']
const statuses: DeclarationStatus[] = ['in-use', 'planned', 'under-consideration', 'to-be-replaced']
const scopes: Scope[] = ['global', 'european', 'national', 'institutional']
const levels: VerificationLevel[] = ['verified', 'under-review', 'self-declared']

export function Glossary() {
    const { t, localized, groupName, statusName, scopeName, verificationName } = useI18n()
    const { hash } = useLocation()
    const principles = useAsync(fetchPrinciples, [])
    const types = useAsync(fetchResourceTypes, [])
    const ready = Boolean(principles.data && types.data)

    useEffect(() => {
        if (hash && ready) {
            document.getElementById(hash.slice(1))?.scrollIntoView()
        }
    }, [hash, ready])

    const anchorClass = (id: string) =>
        `glossary__item${hash === `#${id}` ? ' glossary__item--target' : ''}`

    const values: [string, [string, string][]][] = [
        ['status', statuses.map((item) => [statusName(item), t(`glossary.status.${item}`)])],
        ['scope', scopes.map((item) => [scopeName(item), t(`glossary.scope.${item}`)])],
        [
            'verification',
            levels.map((item) => [verificationName(item), t(`glossary.verification.${item}`)]),
        ],
    ]

    return (
        <PageShell>
            <PageHead title={t('glossary.title')} lead={t('glossary.lead')} />

            <Section title={t('glossary.terms')}>
                <dl className="glossary">
                    {terms.map((term) => (
                        <div key={term} id={term} className={anchorClass(term)}>
                            <dt>{t(`glossary.${term}.title`)}</dt>
                            <dd>{t(`glossary.${term}.text`)}</dd>
                        </div>
                    ))}
                </dl>
            </Section>

            <div id="principles">
                <Section title={t('glossary.principles')} hint={t('glossary.principlesHint')}>
                    {groups.map((group) => (
                        <div className="group" key={group}>
                            <div className="group__head">
                                <span className={`group__badge group__badge--${group}`}>
                                    {group}
                                </span>
                                <h3 style={{ margin: 0 }}>{groupName(group)}</h3>
                                <span className="section__hint">
                                    {t(`glossary.group.${group}`)}
                                </span>
                            </div>
                            <div className="card">
                                {(principles.data ?? [])
                                    .filter((item) => item.group === group)
                                    .map((principle) => (
                                        <div
                                            className="declaration"
                                            key={principle.id}
                                            id={principle.id}
                                        >
                                            <div className="declaration__principle">
                                                <span className="declaration__code">
                                                    {principle.id}
                                                </span>
                                                <Link to={`/resources?principles=${principle.id}`}>
                                                    <span className="declaration__title">
                                                        {t('glossary.findResources')} →
                                                    </span>
                                                </Link>
                                            </div>
                                            <div>
                                                <strong>{localized(principle.title)}</strong>
                                                <p style={{ margin: '0.15rem 0' }}>
                                                    {localized(principle.explanation)}
                                                </p>
                                                <p className="declaration__note">
                                                    {t('glossary.official')}:{' '}
                                                    {localized(principle.statement)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </Section>
            </div>

            <div id="category" className={anchorClass('category')}>
                <Section title={t('glossary.categories')} hint={t('glossary.category.text')}>
                    <dl className="glossary">
                        {(types.data ?? []).map((type) => (
                            <div key={type.id} className="glossary__item">
                                <dt>
                                    {type.icon} {localized(type.label)}
                                </dt>
                                <dd>{localized(type.description)}</dd>
                            </div>
                        ))}
                    </dl>
                </Section>
            </div>

            <Section title={t('glossary.values')}>
                <div className="glossary">
                    {values.map(([key, items]) => (
                        <div key={key} id={key} className={anchorClass(key)}>
                            <h3>{t(`glossary.${key}.title`)}</h3>
                            <p className="section__hint">{t(`glossary.${key}.text`)}</p>
                            <dl className="glossary__values">
                                {items.map(([name, text]) => (
                                    <div key={name}>
                                        <dt>{name}</dt>
                                        <dd>{text}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    ))}
                </div>
            </Section>
        </PageShell>
    )
}
